import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Modal from '@mui/material/Modal';
import { GIF_MAX_DURATION } from '../../constants';
import logoSite from '../../../assets/brand/logo_site.svg';
import { ROUTES } from '../../../constants/routes';
import ExportPreview, { BOUNCERS, BOX_STYLE, ONE_BY_ONE, captionWords, hiddenStyle, lookStyle, wordStyle } from './ExportPreview';
import logoOnLight from '../../../assets/brand/logo_site_onlight.svg';
import { DEFAULT_VIEW, MAX_ZOOM, cropWindow, imageStyle, isCustomLayout } from './framing';
import { LOGO_BACKDROPS, LOGO_SPOTS } from './logoPlace';
import { useClipFrames } from './clipFrames';
import { LOGO_DEFAULTS, canUseMyStyle, loadMyStyle, logoForExport, saveMyStyle, styleToWindow, uploadLogo, windowToStyle } from './myStyle';
import { presetByKey } from './captionPresets';
import PresetPicker from './PresetPicker';
import {
  CAPTION_FONTS,
  CAPTION_FONTS_CSS,
  CAPTION_LOOKS,
  CAPTION_HIGHLIGHTS,
  CAPTION_REVEALS,
  CAPTION_BOUNCES,
  CAPTION_RES,
  INTROS,
  TRANSITIONS,
  CAPTION_MODES,
  CAPTION_PALETTES,
  CAPTION_SIZES,
  EXPORT_TEXT,
  NO_CAPTIONS,
  SHAPES,
  SHAPE_DETAIL,
  describeOption,
} from './exportText';

// The ET export window: what you get (format cards), its shape, captions — and on the
// right a live preview of the first ticked clip plus a plain "You'll get" list, so nobody
// exports blind. The options, the ticking (onOptionChange) and the export call
// (onCreateMedia) are the same ones the old dialog used; only the face changed.

const CHERRY = '#EC2B57';
const MUTED = '#a79fae';
const FAINT = '#756d7c';

const label = {
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '0.6px',
  textTransform: 'uppercase',
  color: MUTED,
};

const cardSx = (on, disabled) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '4px',
  p: '11px 12px',
  minHeight: 74,
  borderRadius: '12px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  textAlign: 'left',
  font: 'inherit',
  color: 'inherit',
  opacity: disabled ? 0.45 : 1,
  background: on ? 'rgba(236,43,87,0.13)' : 'rgba(255,255,255,0.03)',
  border: `1.5px solid ${on ? CHERRY : 'rgba(255,255,255,0.08)'}`,
  transition: 'background 120ms, border-color 120ms',
  '&:hover': disabled ? {} : { borderColor: on ? CHERRY : 'rgba(255,255,255,0.22)' },
  '&:focus-visible': { outline: '2px solid #ff6b8b', outlineOffset: '2px' },
});

const pillSx = (on) => ({
  font: 'inherit',
  height: 34,
  px: '14px',
  borderRadius: '9px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  background: on ? CHERRY : 'transparent',
  border: `1px solid ${on ? CHERRY : 'rgba(255,255,255,0.12)'}`,
  color: on ? '#fff' : '#d9d2de',
  '&:focus-visible': { outline: '2px solid #ff6b8b', outlineOffset: '2px' },
});

const formatDuration = (ms) => {
  const total = Math.round((ms || 0) / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
};

// A slim slider: thin track filled cherry up to the value, a small white knob.
const sliderSx = (pct) => ({
  flex: 1,
  minWidth: 80,
  height: 4,
  borderRadius: 2,
  appearance: 'none',
  WebkitAppearance: 'none',
  outline: 'none',
  cursor: 'pointer',
  background: `linear-gradient(to right, ${CHERRY} ${pct}%, rgba(255,255,255,0.14) ${pct}%)`,
  '&::-webkit-slider-thumb': { WebkitAppearance: 'none', width: 14, height: 14, borderRadius: '50%', background: '#fff', border: 'none', boxShadow: '0 0 0 3px rgba(236,43,87,0.35), 0 1px 3px rgba(0,0,0,0.6)' },
  '&::-moz-range-thumb': { width: 14, height: 14, borderRadius: '50%', background: '#fff', border: 'none', boxShadow: '0 0 0 3px rgba(236,43,87,0.35), 0 1px 3px rgba(0,0,0,0.6)' },
  '&:focus-visible::-webkit-slider-thumb': { boxShadow: '0 0 0 3px #ff6b8b' },
});

/**
 * A pick in the strip, drawn the way it will come out: its own zoom/pan, and (captions on) a
 * tiny caption where that clip's captions sit — in the same words, colours and font as the big
 * preview — so the clips can be compared at a glance.
 */
const PickThumb = ({ src, layout, captions, text, palette, vertical, mode, wordsPerGroup, font, look, upper, highlight, reveal, bounce }) => {
  const [aspect, setAspect] = useState(16 / 9);
  const TILE_ASPECT = 108 / 58;
  const win = cropWindow(aspect, TILE_ASPECT, layout && layout.view);
  const spot = layout && layout.caption;
  const words = captionWords(text, mode, wordsPerGroup);
  return (
    <>
      {src ? (
        <img
          src={src}
          alt=""
          draggable={false}
          onLoad={(e) => {
            const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
            if (w && h) setAspect(w / h);
          }}
          style={imageStyle(win)}
        />
      ) : null}
      {captions && words.length ? (
        <Box
          data-testid="export-pick-caption"
          sx={{
            position: 'absolute',
            left: spot ? `${spot.x * 100}%` : '50%',
            top: spot ? `${spot.y * 100}%` : (vertical ? '74%' : '84%'),
            transform: 'translate(-50%, -50%)',
            maxWidth: '92%',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: 9,
            fontFamily: font ? font.css : undefined,
            fontWeight: font ? font.weight : 800,
            lineHeight: 1.1,
            textTransform: upper ? 'uppercase' : 'none',
            // Same look as the preview; the outline a touch heavier so 9px text stays readable.
            ...(look === 'box' || look === 'shadow' ? lookStyle(look) : { WebkitTextStroke: look === 'thick' ? '0.35em #000' : '0.25em #000', paintOrder: 'stroke fill' }),
            pointerEvents: 'none',
          }}
        >
          <span style={look === 'box' ? BOX_STYLE : undefined}>
            {words.map((w, i) => (
              <React.Fragment key={i}>
                {i ? ' ' : ''}
                <span style={{
                  ...wordStyle(highlight, w.lit, palette, i < words.findIndex((x) => x.lit)),
                  ...(ONE_BY_ONE.includes(reveal) && i > words.findIndex((x) => x.lit) ? hiddenStyle : {}),
                  ...(w.lit && BOUNCERS.includes(bounce) ? { position: 'relative', display: 'inline-block' } : {}),
                }}
                >
                  {w.text}
                  {w.lit && bounce === 'ball' ? (
                    <span aria-hidden style={{ position: 'absolute', left: '50%', bottom: '100%', width: '0.42em', height: '0.42em', marginLeft: '-0.21em', borderRadius: '50%', background: palette.active, border: '1px solid #000', boxSizing: 'border-box' }} />
                  ) : null}
                  {w.lit && bounce === 'cherry' ? (
                    <img src={logoSite} alt="" aria-hidden style={{ position: 'absolute', left: '50%', bottom: '100%', height: '1em', transform: 'translateX(-50%)' }} />
                  ) : null}
                  {w.lit && bounce === 'cherryball' ? (
                    <span aria-hidden style={{ position: 'absolute', left: '50%', bottom: '100%', width: '0.8em', height: '0.8em', marginLeft: '-0.4em', borderRadius: '50%', background: '#fff', border: '1px solid #000', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img src={logoOnLight} alt="" style={{ height: '72%' }} />
                    </span>
                  ) : null}
                </span>
              </React.Fragment>
            ))}
          </span>
        </Box>
      ) : null}
    </>
  );
};

/** A labelled row of single-choice pills (the caption and transition options). */
const PillRow = ({ label, items, value, onChange, disabled = false, hint = '' }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }} role="group" aria-label={label}>
    <Box component="span" sx={{ fontSize: 12, color: MUTED }}>{label}</Box>
    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', opacity: disabled ? 0.4 : 1 }}>
      {items.map((it) => (
        <Box component="button" key={it.key} aria-pressed={value === it.key} disabled={disabled} onClick={() => onChange(it.key)} sx={pillSx(value === it.key)}>
          {it.icon ? <img src={it.icon} alt="" aria-hidden style={{ height: 14, marginRight: 5, verticalAlign: '-2px' }} /> : null}
          {it.label}
        </Box>
      ))}
    </Box>
    {hint ? <Box component="span" sx={{ fontSize: 11, color: FAINT }}>{hint}</Box> : null}
  </Box>
);

const disabledReason = (card) => {
  if (card.key === 'super') return EXPORT_TEXT.NEEDS_SEARCH;
  if (card.key === 'gif') return EXPORT_TEXT.gifLimit(GIF_MAX_DURATION / 1000);
  return EXPORT_TEXT.NEEDS_CLIPS;
};

const ExportWindow = ({
  open,
  onClose,
  options = [],
  selectedOptions = [],
  onOptionChange,
  onCreateMedia,
  defaultExportTitle = '',
  selectedDurationMs = 0,
  preview = {},
  // Optional: copy/share the spreadsheet link (the ET's share-box action). No handler, no button.
  onShareLink = null,
  // Optional: (id, text|null) — edit a clip's words, the same edit as the ET's ✏ (null = back to
  // what was said). No handler, no ✏ Edit words.
  onEditText = null,
}) => {
  const [title, setTitle] = useState(defaultExportTitle);
  const [shape, setShape] = useState('original');
  const [captions, setCaptions] = useState(false);
  const [mode, setMode] = useState('word');
  const [wordsPerGroup, setWordsPerGroup] = useState(3);
  const [paletteKey, setPaletteKey] = useState('cherry');
  const [sizeKey, setSizeKey] = useState('M');
  const [fontKey, setFontKey] = useState('heavy');
  const [lookKey, setLookKey] = useState('outline');
  const [upper, setUpper] = useState(false);
  const [highlightKey, setHighlightKey] = useState('color');
  const [revealKey, setRevealKey] = useState('all');
  const [bounceKey, setBounceKey] = useState('none');
  // A one-click caption preset ('' = your own mix of the choices). Choosing words / highlight /
  // appear / bounce yourself switches it off (that is what a preset IS); font, colours, size,
  // caps and look stay inside it.
  const [presetKey, setPresetKey] = useState('');
  const [resKey, setResKey] = useState('hd');
  const [introKey, setIntroKey] = useState('none');
  const [transitionKey, setTransitionKey] = useState('none');
  // A transition per join: { "<pick id>><next pick id>": key } over the Between clips choice.
  const [gapKeys, setGapKeys] = useState({});
  const [gapMenu, setGapMenu] = useState(null); // { anchor, index } — the chip being changed
  const [focus, setFocus] = useState(null);
  const [separate, setSeparate] = useState(false);
  const [previewId, setPreviewId] = useState(null);
  // Reposition: per-clip framing { [pickId]: { view: {x,y,zoom,face}, caption: {x,y} } } and
  // where the captions sit on every clip without its own spot (null = the usual place).
  const [layouts, setLayouts] = useState({});
  // What Copy to next / Apply to all carry over: the picture's framing, the captions' spot, or both.
  const [copyParts, setCopyParts] = useState({ picture: true, captions: true });
  // Captions are placed per clip like the picture (drag on the clip on show, then Copy to next /
  // Apply to all). The worker still accepts one export-wide spot; the window doesn't need it.
  const captionPos = null;
  const [shared, setShared] = useState(null); // { url, copied }
  // My Style: the window opens once with the account's saved look, and can save the current one.
  const [myStyle, setMyStyle] = useState(null);
  const [styleNote, setStyleNote] = useState('');
  // Your logo: {key, url, x, y, size, opacity} or null.
  const [logo, setLogo] = useState(null);
  const [logoNote, setLogoNote] = useState('');
  const logoInput = useRef(null);
  const styleLoaded = useRef(false);
  // Super Cut handles are deliberate, per export: both start off every time the window opens
  // (exact word cuts), so nothing extra sneaks into the next supercut.
  const [padMs, setPadMs] = useState(0);
  const [equalize, setEqualize] = useState(false);

  // Drag the window by its header so it doesn't sit on top of your picks.
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const drag = useRef(null);
  const onDragStart = (e) => {
    if (e.button !== 0 || e.target.closest('button, input, a')) return;
    drag.current = { x: e.clientX - offset.x, y: e.clientY - offset.y };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onDragMove = (e) => {
    if (!drag.current) return;
    const maxX = window.innerWidth / 2;
    const maxY = window.innerHeight / 2 - 40;
    setOffset({
      x: Math.max(-maxX, Math.min(maxX, e.clientX - drag.current.x)),
      y: Math.max(-maxY, Math.min(maxY, e.clientY - drag.current.y)),
    });
  };
  const onDragEnd = () => { drag.current = null; };

  useEffect(() => {
    if (open) setTitle(defaultExportTitle);
  }, [open, defaultExportTitle]);

  // The first time the window opens, start from the saved style (if any). After that the
  // window keeps whatever was last chosen in it, as before.
  useEffect(() => {
    if (!open || styleLoaded.current) return undefined;
    let cancelled = false;
    loadMyStyle().then((style) => {
      // Marked loaded only when an answer is used: a cancelled run (closed early, or React's
      // dev double-run of effects) must leave the next run free to load it again.
      if (cancelled) return;
      styleLoaded.current = true;
      if (!style) return;
      const w = styleToWindow(style);
      const apply = [
        ['shape', setShape], ['captions', setCaptions], ['mode', setMode], ['wordsPerGroup', setWordsPerGroup],
        ['paletteKey', setPaletteKey], ['sizeKey', setSizeKey], ['fontKey', setFontKey], ['lookKey', setLookKey],
        ['upper', setUpper], ['highlightKey', setHighlightKey], ['revealKey', setRevealKey],
        ['bounceKey', setBounceKey], ['resKey', setResKey], ['introKey', setIntroKey], ['transitionKey', setTransitionKey],
        ['logo', setLogo], ['presetKey', setPresetKey],
      ];
      apply.forEach(([key, set]) => { if (w[key] !== undefined) set(w[key]); });
      setMyStyle(style);
      setStyleNote(EXPORT_TEXT.STYLE_IN_USE);
    });
    return () => { cancelled = true; };
  }, [open]);

  const pickLogo = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setLogoNote(EXPORT_TEXT.LOGO_UPLOADING);
    try {
      const up = await uploadLogo(file);
      setLogo((prev) => ({ ...LOGO_DEFAULTS, ...(prev ? { x: prev.x, y: prev.y, size: prev.size, opacity: prev.opacity } : {}), key: up.key, url: up.url }));
      setLogoNote(EXPORT_TEXT.LOGO_SAVE_REMINDER);
    } catch (err) {
      setLogoNote(EXPORT_TEXT.LOGO_FAILED);
    }
  };

  const doSaveStyle = async () => {
    try {
      const kept = await saveMyStyle(windowToStyle({
        shape, captions, mode, wordsPerGroup, paletteKey, sizeKey, fontKey, lookKey, upper,
        highlightKey, revealKey, bounceKey, resKey, introKey, transitionKey, logo, presetKey,
      }));
      setMyStyle(kept);
      setStyleNote(EXPORT_TEXT.STYLE_SAVED);
    } catch (e) {
      setStyleNote(EXPORT_TEXT.STYLE_SAVE_FAILED);
    }
  };

  // The caption fonts, so the preview draws the same letters the worker burns.
  useEffect(() => {
    if (!open || typeof document === 'undefined' || document.getElementById('cc-caption-fonts')) return;
    const link = document.createElement('link');
    link.id = 'cc-caption-fonts';
    link.rel = 'stylesheet';
    link.href = CAPTION_FONTS_CSS;
    document.head.appendChild(link);
  }, [open]);

  // On opening (on a computer), the title is focused with its text selected — the first thing
  // to look at, and typing replaces it. Not on touch screens: it would pop the keyboard up.
  const titleRef = useRef(null);
  useEffect(() => {
    if (!open || typeof window === 'undefined' || !window.matchMedia?.('(pointer: fine)').matches) return undefined;
    const t = setTimeout(() => {
      const el = titleRef.current;
      if (el) {
        el.focus();
        // Select it all with the caret at the START, so a long title shows its beginning.
        el.setSelectionRange(0, el.value.length, 'backward');
        el.scrollLeft = 0;
      }
    }, 50);
    return () => clearTimeout(t);
  }, [open]);

  // "More options ↓": the choices outgrow the window (Shape and Captions sit below the
  // format cards), so say so until the bottom is in view. Whichever box scrolls — the left
  // column on a computer, the whole window on a phone — is the one watched.
  const paperRef = useRef(null);
  const leftRef = useRef(null);
  const [moreBelow, setMoreBelow] = useState(false);
  const scroller = () => {
    const left = leftRef.current;
    if (left && left.scrollHeight - left.clientHeight > 4) return left;
    return paperRef.current;
  };
  const checkMore = useCallback(() => {
    const el = scroller();
    if (!el) return;
    setMoreBelow(el.scrollHeight - el.clientHeight - el.scrollTop > 24);
  }, []);
  useEffect(() => {
    if (!open) return;
    setOffset({ x: 0, y: 0 });
    setPadMs(0);
    setEqualize(false);
    setLayouts({});
    setGapKeys({});
  }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(checkMore, 0);
    window.addEventListener('resize', checkMore);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', checkMore);
    };
  }, [open, checkMore, selectedOptions, captions, mode]);
  const scrollDown = () => {
    const el = scroller();
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  };

  const cards = useMemo(
    () =>
      options.map((option) => {
        const name = Array.isArray(option) ? option[0] : option;
        const enabled = Array.isArray(option) ? Boolean(option[1]) : true;
        return { name, enabled, ...describeOption(name) };
      }),
    [options],
  );
  const chosen = cards.filter((c) => selectedOptions.includes(c.name));
  const makesVideo = chosen.some((c) => c.video);
  const takesCaptions = chosen.some((c) => c.caps);
  const makesSupercut = chosen.some((c) => c.key === 'super');
  // Breathing room: every export that cuts the clips' sound or picture.
  const takesRoom = chosen.some((c) => ['video', 'audiogram', 'gif', 'mp3', 'xml'].includes(c.key));
  const focusCard = chosen.find((c) => c.name === focus) || chosen[chosen.length - 1];
  const palette = CAPTION_PALETTES.find((p) => p.key === paletteKey) || CAPTION_PALETTES[0];
  const size = (CAPTION_SIZES.find((s) => s.key === sizeKey) || CAPTION_SIZES.find((s) => s.key === 'M')).size;
  const shapeDef = SHAPES.find((s) => s.key === shape) || SHAPES[0];
  const count = preview.count || 0;
  // A split piece's poster is its parent's first frame — swap in its own frame once grabbed.
  const frames = useClipFrames(open ? (preview.picks || []).filter((p) => p.frameAt != null && p.video).map((p) => ({ id: p.id, url: p.video, t: p.frameAt })) : []);
  const picks = useMemo(
    () => (preview.picks || []).map((p) => (frames[p.id] ? { ...p, thumbnail: frames[p.id] } : p)),
    [preview.picks, frames],
  );
  const shown = picks.find((p) => p.id === previewId) || picks[0] || preview;
  const shownIndex = picks.findIndex((p) => p.id === shown.id);
  // A style tile clicked: that preset's whole look ('' = your own mix: the choices stay as they are).
  const pickPreset = (key) => {
    setPresetKey(key);
    const p = presetByKey(key);
    if (!p) return;
    const w = styleToWindow(p.fields);
    [
      ['mode', setMode], ['wordsPerGroup', setWordsPerGroup], ['paletteKey', setPaletteKey], ['sizeKey', setSizeKey],
      ['fontKey', setFontKey], ['lookKey', setLookKey], ['upper', setUpper], ['highlightKey', setHighlightKey],
      ['revealKey', setRevealKey], ['bounceKey', setBounceKey],
    ].forEach(([k, set]) => { if (w[k] !== undefined) set(w[k]); });
  };
  // Words / highlight / appear / bounce chosen by hand: your own mix from here on.
  const ownMotion = (set) => (value) => {
    set(value);
    setPresetKey('');
  };
  // What a style tile draws: the chosen one (and "Your own") = the window as it is now, so it
  // follows a font or colour change; any other preset = its own look.
  const lookFor = (key) => {
    const p = key === presetKey ? null : presetByKey(key);
    const w = p ? styleToWindow(p.fields) : { mode, wordsPerGroup, paletteKey, sizeKey, fontKey, lookKey, upper, highlightKey, revealKey, bounceKey };
    return {
      mode: w.mode,
      wordsPerGroup: w.wordsPerGroup,
      palette: CAPTION_PALETTES.find((c) => c.key === w.paletteKey) || CAPTION_PALETTES[0],
      size: (CAPTION_SIZES.find((c) => c.key === w.sizeKey) || CAPTION_SIZES.find((c) => c.key === 'M')).size,
      font: CAPTION_FONTS.find((f) => f.key === w.fontKey),
      look: w.lookKey,
      upper: w.upper,
      highlight: w.highlightKey,
      reveal: w.revealKey,
      bounce: w.bounceKey,
      preset: key,
    };
  };
  const currentId = shownIndex >= 0 ? shown.id : null;
  const currentLayout = (currentId !== null && layouts[currentId]) || {};
  const currentView = currentLayout.view || DEFAULT_VIEW;
  const setCurrent = (patch) => {
    if (currentId === null) return;
    setLayouts((prev) => ({ ...prev, [currentId]: { ...(prev[currentId] || {}), ...patch } }));
  };
  const setView = (view) => setCurrent({ view });
  const moveCaption = (pos) => setCurrent({ caption: pos });
  // Carry the chosen parts of the clip on show onto `target` (a part this clip doesn't
  // have is cleared on the target too — copying "the default" resets it).
  const carry = (target) => {
    const src = layouts[currentId] || {};
    const next = { ...(target || {}) };
    if (copyParts.picture) {
      if (src.view) next.view = { ...src.view }; else delete next.view;
    }
    if (copyParts.captions && captions) {
      if (src.caption) next.caption = { ...src.caption }; else delete next.caption;
    }
    return next;
  };
  const copyToNext = () => {
    const next = picks[shownIndex + 1];
    if (!next) return;
    setLayouts((prev) => ({ ...prev, [next.id]: carry(prev[next.id]) }));
    setPreviewId(next.id);
  };
  const applyToAll = () => {
    setLayouts((prev) => Object.fromEntries(picks.map((p) => [p.id, p.id === currentId ? (prev[p.id] || {}) : carry(prev[p.id])])));
  };
  const resetAll = () => setLayouts({});

  // ✏ Edit words — the clip on show, the same edit as the ET's ✏.
  const canEditText = Boolean(onEditText) && currentId !== null && makesVideo;
  const [editingText, setEditingText] = useState(null);
  useEffect(() => { setEditingText(null); }, [open, currentId]);
  // ▶ Play clip: the clip on show plays in the preview (sound on); off whenever the window opens.
  const [playing, setPlaying] = useState(false);
  useEffect(() => { if (!open) setPlaying(false); }, [open]);
  const canPlay = Boolean(shown && shown.video) && ['video', 'super', 'audiogram'].includes(focusCard?.key);
  const startEditing = () => setEditingText(shown.text || '');
  const saveEditing = () => {
    if (editingText === null || currentId === null) return;
    onEditText(currentId, editingText);
    setEditingText(null);
  };
  const copyAnything = copyParts.picture || (copyParts.captions && captions);
  const resetClip = () => setLayouts((prev) => {
    const next = { ...prev };
    delete next[currentId];
    return next;
  });
  const toggleFace = () => {
    if (currentView.face) {
      // Off: back exactly to the framing you had before turning it on.
      const { before, ...rest } = currentView;
      setView({ ...rest, ...(before || { x: 0.5, y: 0.5, zoom: 1 }), face: false });
      return;
    }
    // On: remember the current framing; a wide shape needs a little punch-in to show the aim.
    const before = { x: currentView.x, y: currentView.y, zoom: currentView.zoom };
    const zoom = shape !== 'zoom' && currentView.zoom <= 1 ? 1.5 : currentView.zoom;
    setView({ ...currentView, face: true, zoom, before });
  };
  const framedCount = picks.filter((p) => isCustomLayout(layouts[p.id])).length;
  const videoCards = chosen.filter((c) => c.video);
  const canSeparate = videoCards.length > 0 && picks.length > 1;
  const isSeparate = separate && canSeparate;
  // The transition at each join (pick i → pick i+1): its own choice, else Between clips.
  const pairKey = (i) => `${picks[i].id}>${picks[i + 1].id}`;
  const gapOf = (i) => gapKeys[pairKey(i)] ?? transitionKey;
  const gapList = picks.slice(1).map((_, i) => gapOf(i));
  const gapsOn = !isSeparate && picks.length > 1 && chosen.some((c) => c.key === 'video' || c.key === 'audiogram');
  const setGap = (i, key) => setGapKeys((prev) => ({ ...prev, [pairKey(i)]: key }));
  const chooseBetween = (key) => {
    setTransitionKey(key);
    setGapKeys({}); // Between clips sets every join
  };
  const fileCount = chosen.length + (isSeparate ? videoCards.length * (picks.length - 1) : 0);
  const duration = formatDuration(selectedDurationMs);
  const fxBits = [
    captions && resKey === '4k' ? CAPTION_RES.find((r) => r.key === '4k').detail : '',
    introKey !== 'none' ? `${INTROS.find((x) => x.key === introKey).label.toLowerCase()} start` : '',
    !isSeparate && picks.length > 1 && gapList.some((k) => k !== 'none')
      ? (new Set(gapList).size > 1 ? EXPORT_TEXT.MIXED_BETWEEN : `${TRANSITIONS.find((x) => x.key === gapList[0]).label.toLowerCase()} between clips`)
      : '',
  ].filter(Boolean);
  const captionDetail = (captions ? CAPTION_MODES.find((m) => m.key === mode).detail : NO_CAPTIONS)
    + (fxBits.length ? ` · ${fxBits.join(' · ')}` : '');

  const toggle = (card) => {
    if (!card.enabled) return;
    const on = !selectedOptions.includes(card.name);
    onOptionChange(card.name, on);
    if (on) setFocus(card.name);
  };

  const summary = chosen.map((c) => {
    let detail;
    const lead = c.video && isSeparate ? EXPORT_TEXT.videosCount(picks.length) : duration;
    const room = padMs ? ` · ${EXPORT_TEXT.roomDetail(padMs)}` : '';
    if (c.key === 'video') detail = `${lead} · ${SHAPE_DETAIL[shape]} · ${captionDetail}${room}`;
    else if (c.key === 'super') detail = `searched words only · ${padMs ? `+${padMs} ms` : 'exact cuts'}${equalize ? ' · same length' : ''} · ${SHAPE_DETAIL[shape]} · ${captionDetail}`;
    else if (c.key === 'audiogram') detail = `${lead} · ${shapeDef.vertical ? '1080×1920' : '1920×1080'} · ${captionDetail}${room}`;
    else if (c.key === 'gif') detail = `silent loop${room}`;
    else if (c.key === 'mp3') detail = `${duration} of sound${room}`;
    else if (c.key === 'srt' || c.key === 'vtt') detail = 'timed text';
    else if (c.key === 'xml') detail = `a Premiere sequence${room}`;
    else if (c.key === 'zip') detail = EXPORT_TEXT.ZIP_MORE;
    else if (c.key === 'txt') detail = 'a text document · speakers and times';
    else detail = '';
    return { name: c.name, ext: c.ext, label: c.label, detail };
  });

  const exportLabel =
    chosen.length === 0
      ? EXPORT_TEXT.EXPORT
      : isSeparate && chosen.length === 1
        ? EXPORT_TEXT.exportVideos(picks.length)
        : fileCount === 1
          ? EXPORT_TEXT.exportOne(chosen[0].ext || chosen[0].label)
          : EXPORT_TEXT.exportFiles(fileCount);

  const doShare = async () => {
    const url = await onShareLink();
    if (typeof url === 'string' && url) setShared({ url, copied: false });
  };
  const copyShared = async () => {
    try {
      await navigator.clipboard.writeText(shared.url);
      setShared((v) => ({ ...v, copied: true }));
    } catch (e) {
      /* the link is on screen to copy by hand */
    }
  };

  const doExport = () => {
    if (!chosen.length) return;
    onCreateMedia(
      selectedOptions,
      title.trim() || null,
      shapeDef.orientation,
      captions,
      mode,
      size,
      wordsPerGroup,
      paletteKey,
      isSeparate,
      fontKey,
      { padMs, equalize },
      {
        layouts: picks
          .filter((p) => isCustomLayout(layouts[p.id]))
          .map((p) => ({ id: p.id, ...layouts[p.id] })),
        captionPos,
      },
      { look: lookKey, upper, highlight: highlightKey, reveal: revealKey, bounce: bounceKey, res: resKey, ...(presetKey ? { preset: presetKey } : {}) },
      {
        intro: introKey,
        transition: isSeparate ? 'none' : transitionKey,
        // one per join, only when a join differs from Between clips
        transitions: gapsOn && gapList.some((k) => k !== transitionKey) ? gapList : null,
        ...(logo && logo.key ? { logo: logoForExport(logo) } : {}),
      },
    );
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      disableScrollLock
      BackdropProps={{ sx: { backgroundColor: 'rgba(8,6,10,0.35)' } }}
    >
      <Box
        ref={paperRef}
        onScroll={checkMore}
        role="dialog"
        aria-label={count === 1 ? EXPORT_TEXT.TITLE_ONE : EXPORT_TEXT.titleMany(count)}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: { xs: 'translate(-50%, -50%)', md: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))` },
          width: { xs: '94vw', md: 1100 },
          maxWidth: '96vw',
          height: { xs: '92vh', md: 'min(760px, 92vh)' },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          overflowY: { xs: 'auto', md: 'hidden' },
          borderRadius: '20px',
          background: '#1a171f',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 30px 80px rgba(0,0,0,0.55)',
          fontFamily: 'Outfit, system-ui, sans-serif',
          color: '#f4f1f6',
          outline: 'none',
        }}
      >
        {/* ---- left: the choices ---- */}
        <Box
          sx={{
            width: { xs: '100%', md: 560 },
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            minHeight: { md: 0 },
            borderRight: { md: '1px solid rgba(255,255,255,0.07)' },
          }}
        >
          <Box
            data-testid="export-drag-handle"
            title={EXPORT_TEXT.DRAG_HINT}
            onPointerDown={onDragStart}
            onPointerMove={onDragMove}
            onPointerUp={onDragEnd}
            onPointerCancel={onDragEnd}
            onDoubleClick={() => setOffset({ x: 0, y: 0 })}
            sx={{ px: 3.5, pt: 2.75, pb: 2, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: '1px solid rgba(255,255,255,0.07)', cursor: { md: 'grab' }, userSelect: 'none', touchAction: 'none' }}
          >
            <img src={logoSite} alt={EXPORT_TEXT.LOGO_ALT} style={{ height: 44, width: 'auto', alignSelf: 'center', marginTop: -6 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ fontSize: 20, fontWeight: 700 }}>
                {count === 1 ? EXPORT_TEXT.TITLE_ONE : EXPORT_TEXT.titleMany(count)}
              </Box>
              <Box sx={{ fontSize: 13, color: selectedDurationMs > 300000 ? '#f59e0b' : MUTED }}>
                {duration} total{selectedDurationMs > 300000 ? ` · ${EXPORT_TEXT.LONG}` : ''}
              </Box>
            </Box>
            <Box
              component="button"
              aria-label={EXPORT_TEXT.CLOSE}
              onClick={onClose}
              sx={{ width: 36, height: 36, borderRadius: '10px', border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: 'inherit', fontSize: 18, cursor: 'pointer' }}
            >
              ×
            </Box>
          </Box>

          <Box
            ref={leftRef}
            onScroll={checkMore}
            sx={{ flex: 1, minHeight: 0, overflowY: { md: 'auto' }, px: 3.5, pt: 2.5, pb: 3, display: 'flex', flexDirection: 'column', gap: 2.75 }}
          >
            <Box component="label" sx={{ ...label, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {EXPORT_TEXT.NAME}
              <Box
                component="input"
                ref={titleRef}
                value={title}
                placeholder={EXPORT_TEXT.NAME_PLACEHOLDER}
                onChange={(e) => setTitle(e.target.value)}
                sx={{
                  height: 40,
                  borderRadius: '10px',
                  border: '1px solid rgba(255,255,255,0.12)',
                  background: '#121015',
                  color: '#f4f1f6',
                  px: 1.5,
                  font: 'inherit',
                  fontSize: 15,
                  textTransform: 'none',
                  letterSpacing: 0,
                  '&:focus': { outline: 'none', borderColor: CHERRY },
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Box component="span" sx={label}>{EXPORT_TEXT.WHAT}</Box>
                <Box component="span" sx={{ fontSize: 12, color: FAINT }}>{EXPORT_TEXT.WHAT_HINT}</Box>
              </Box>
              {/* Scrolls on a computer (about two rows show, the third peeks) so Shape and
                  Captions come into view sooner; the whole list on a phone. */}
              {/* The SCROLL is on this wrapper; the grid inside keeps every card its full height
                  (scrolling the grid itself squashed the cards and their text spilled out). */}
              <Box data-testid="export-formats" sx={{ maxHeight: { md: 200 }, overflowY: { md: 'auto' }, pr: { md: 0.5 } }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
                {cards.map((c) => {
                  const on = selectedOptions.includes(c.name);
                  return (
                    <Box
                      component="button"
                      key={c.name}
                      aria-label={c.label}
                      aria-pressed={on}
                      disabled={!c.enabled}
                      onClick={() => toggle(c)}
                      sx={cardSx(on, !c.enabled)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box component="span" sx={{ fontSize: 15, fontWeight: 600 }}>{c.label}</Box>
                        {c.ext ? (
                          <Box component="span" sx={{ px: 0.75, py: '1px', borderRadius: '5px', fontSize: 10, fontWeight: 700, letterSpacing: '0.4px', background: on ? CHERRY : 'rgba(255,255,255,0.08)', color: on ? '#fff' : MUTED }}>
                            {c.ext}
                          </Box>
                        ) : null}
                      </Box>
                      <Box component="span" sx={{ fontSize: 12, lineHeight: '16px', color: MUTED }}>
                        {c.enabled ? c.desc : disabledReason(c)}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
              </Box>

            </Box>

            {makesVideo ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                <Box component="span" sx={label}>{EXPORT_TEXT.SHAPE}</Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' }, gap: 1 }}>
                  {SHAPES.map((s) => {
                    const on = shape === s.key;
                    const ink = on ? CHERRY : '#8d8594';
                    return (
                      <Box
                        component="button"
                        key={s.key}
                        aria-label={s.label}
                        aria-pressed={on}
                        onClick={() => setShape(s.key)}
                        sx={{ ...cardSx(on, false), alignItems: 'center', textAlign: 'center', minHeight: 108 }}
                      >
                        <Box sx={{ height: 52, display: 'flex', alignItems: 'center' }}>
                          <Box
                            sx={{
                              width: s.vertical ? 26 : 44,
                              height: s.vertical ? 46 : 25,
                              borderRadius: '4px',
                              border: `2px solid ${on ? CHERRY : '#6a6370'}`,
                              p: '2px',
                              boxSizing: 'border-box',
                              display: 'flex',
                              alignItems: 'center',
                              background: s.key === 'blur' ? `${ink}4d` : 'transparent',
                            }}
                          >
                            <Box sx={{ width: '100%', height: s.key === 'blur' ? '40%' : '100%', background: ink, borderRadius: '2px' }} />
                          </Box>
                        </Box>
                        <Box component="span" sx={{ fontSize: 14, fontWeight: 600 }}>{s.label}</Box>
                        <Box component="span" sx={{ fontSize: 11, color: MUTED }}>{s.hint}</Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ) : null}

            {takesCaptions ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: '14px 16px', borderRadius: '14px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ fontSize: 15, fontWeight: 600 }}>{EXPORT_TEXT.CAPTIONS}</Box>
                    <Box sx={{ fontSize: 12, color: MUTED }}>{EXPORT_TEXT.CAPTIONS_HINT}</Box>
                  </Box>
                  <Box
                    component="button"
                    aria-label={EXPORT_TEXT.CAPTIONS}
                    aria-pressed={captions}
                    onClick={() => setCaptions((v) => !v)}
                    sx={{ width: 48, height: 28, borderRadius: '14px', border: 'none', cursor: 'pointer', position: 'relative', p: 0, background: captions ? CHERRY : 'rgba(255,255,255,0.15)', '&:focus-visible': { outline: '2px solid #ff6b8b', outlineOffset: '2px' } }}
                  >
                    <Box component="span" sx={{ position: 'absolute', top: 3, left: captions ? 23 : 3, width: 22, height: 22, borderRadius: '11px', background: '#fff', transition: 'left 120ms' }} />
                  </Box>
                </Box>
                {captions ? (
                  <>
                    <PresetPicker
                      presetKey={presetKey}
                      onPick={pickPreset}
                      sample={{ thumbnail: shown.thumbnail, text: shown.text, nouns: shown.nouns, wordStarts: shown.wordStarts, seconds: shown.seconds }}
                      lookFor={lookFor}
                      vertical={shapeDef.vertical}
                      blur={shape === 'blur'}
                    />
                    <Box sx={{ display: 'flex', gap: 0.75 }}>
                      {CAPTION_MODES.map((m) => (
                        <Box component="button" key={m.key} aria-pressed={mode === m.key} onClick={() => ownMotion(setMode)(m.key)} sx={{ ...pillSx(mode === m.key), flex: 1 }}>
                          {m.label}
                        </Box>
                      ))}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                      <Box component="span" sx={{ fontSize: 12, color: MUTED }}>{EXPORT_TEXT.FONT}</Box>
                      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                        {CAPTION_FONTS.filter((f) => !f.hidden).map((f) => (
                          <Box
                            component="button"
                            key={f.key}
                            aria-pressed={fontKey === f.key}
                            onClick={() => setFontKey(f.key)}
                            sx={{ ...pillSx(fontKey === f.key), fontFamily: f.css, fontWeight: f.weight, fontStyle: f.italic ? 'italic' : 'normal' }}
                          >
                            {f.label}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                      <Box component="span" sx={{ fontSize: 12, color: MUTED }}>{EXPORT_TEXT.COLORS}</Box>
                      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                        {CAPTION_PALETTES.map((p) => (
                          <Box
                            component="button"
                            key={p.key}
                            aria-label={p.label}
                            title={p.label}
                            aria-pressed={paletteKey === p.key}
                            onClick={() => setPaletteKey(p.key)}
                            sx={{ width: 40, height: 34, borderRadius: '9px', cursor: 'pointer', background: '#0f0d12', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1px', font: 'inherit', fontWeight: 800, fontSize: 15, border: paletteKey === p.key ? `2px solid ${CHERRY}` : '1px solid rgba(255,255,255,0.12)' }}
                          >
                            <span style={{ color: p.base, fontWeight: 800 }}>A</span>
                            <span style={{ color: p.active, fontWeight: 800 }}>a</span>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                      <Box component="span" sx={{ fontSize: 12, color: MUTED }}>{EXPORT_TEXT.SIZE}</Box>
                      <Box sx={{ display: 'flex', gap: 0.75 }}>
                        {CAPTION_SIZES.map((s) => (
                          <Box component="button" key={s.key} aria-pressed={sizeKey === s.key} onClick={() => setSizeKey(s.key)} sx={{ ...pillSx(sizeKey === s.key), width: 40, px: 0 }}>
                            {s.key}
                          </Box>
                        ))}
                      </Box>
                      <Box component="label" sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 12, whiteSpace: 'nowrap', color: '#d9d2de', cursor: 'pointer' }}>
                        <Box component="input" type="checkbox" checked={upper} onChange={() => setUpper((v) => !v)} sx={{ m: 0, accentColor: '#8d8594', cursor: 'inherit' }} />
                        {EXPORT_TEXT.CAPS}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                      <Box component="span" sx={{ fontSize: 12, color: MUTED }}>{EXPORT_TEXT.STYLE}</Box>
                      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                        {CAPTION_LOOKS.map((l) => (
                          <Box component="button" key={l.key} aria-pressed={lookKey === l.key} onClick={() => setLookKey(l.key)} sx={pillSx(lookKey === l.key)}>
                            {l.label}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                    <PillRow label={EXPORT_TEXT.HIGHLIGHT} items={CAPTION_HIGHLIGHTS} value={highlightKey} onChange={ownMotion(setHighlightKey)} />
                    <PillRow label={EXPORT_TEXT.APPEAR} items={CAPTION_REVEALS} value={revealKey} onChange={ownMotion(setRevealKey)} />
                    <PillRow
                      label={EXPORT_TEXT.BOUNCE}
                      items={CAPTION_BOUNCES.map((b) => (b.key === 'cherry' ? { ...b, icon: logoSite } : b))}
                      value={bounceKey}
                      onChange={ownMotion(setBounceKey)}
                    />
                    <PillRow label={EXPORT_TEXT.SHARPNESS} items={CAPTION_RES} value={resKey} onChange={setResKey} />
                    {mode === 'group' ? (
                      <Box component="label" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 12, color: MUTED }}>
                        {EXPORT_TEXT.WORDS_PER_GROUP}
                        <Box
                          component="input"
                          type="number"
                          min={1}
                          max={10}
                          value={wordsPerGroup}
                          onChange={(e) => ownMotion(setWordsPerGroup)(Math.max(1, Math.min(10, parseInt(e.target.value, 10) || 3)))}
                          sx={{ width: 56, height: 30, borderRadius: '8px', border: '1px solid rgba(255,255,255,0.12)', background: '#121015', color: '#f4f1f6', px: 1, font: 'inherit' }}
                        />
                      </Box>
                    ) : null}
                  </>
                ) : null}
              </Box>
            ) : null}
            {makesVideo ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: '14px 16px', borderRadius: '14px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <Box>
                  <Box sx={{ fontSize: 15, fontWeight: 600 }}>{EXPORT_TEXT.TRANSITIONS}</Box>
                  <Box sx={{ fontSize: 12, color: MUTED }}>{EXPORT_TEXT.TRANSITIONS_HINT}</Box>
                </Box>
                <PillRow label={EXPORT_TEXT.START} items={INTROS} value={introKey} onChange={setIntroKey} />
                <PillRow
                  label={EXPORT_TEXT.BETWEEN}
                  items={TRANSITIONS}
                  value={isSeparate || picks.length < 2 ? 'none' : transitionKey}
                  onChange={chooseBetween}
                  disabled={isSeparate || picks.length < 2}
                  hint={isSeparate || picks.length < 2 ? EXPORT_TEXT.BETWEEN_OFF : ''}
                />
              </Box>
            ) : null}
            {/* How comes after Shape and Captions, so those two show sooner. */}
            {canSeparate ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                <Box component="span" sx={label}>{EXPORT_TEXT.HOW}</Box>
                <Box sx={{ display: 'flex', gap: 0.75 }}>
                  {[[false, EXPORT_TEXT.STITCHED], [true, EXPORT_TEXT.SEPARATE]].map(([value, text]) => (
                    <Box component="button" key={text} aria-pressed={separate === value} onClick={() => setSeparate(value)} sx={{ ...pillSx(separate === value), flex: 1, height: 40 }}>
                      {text}
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : null}
            {/* Your logo and breathing room come last: captions first (Isaac, 10-18). */}
            {makesVideo && canUseMyStyle() ? (
              <Box data-testid="export-logo" sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, p: '14px 16px', borderRadius: '14px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {logo && logo.url ? (
                    <Box component="img" src={logo.url} alt="" sx={{ width: 44, height: 44, objectFit: 'contain', borderRadius: '8px', background: 'rgba(255,255,255,0.06)', flexShrink: 0 }} />
                  ) : null}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ fontSize: 15, fontWeight: 600 }}>{EXPORT_TEXT.LOGO}</Box>
                    <Box sx={{ fontSize: 12, color: MUTED }}>{logoNote || EXPORT_TEXT.LOGO_HINT}</Box>
                  </Box>
                  <input ref={logoInput} id="cc-logo-file" type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={pickLogo} />
                  <Box component="button" type="button" onClick={() => logoInput.current && logoInput.current.click()} sx={{ ...pillSx(false), height: 30, fontSize: 13 }}>
                    {logo ? EXPORT_TEXT.LOGO_REPLACE : EXPORT_TEXT.LOGO_UPLOAD}
                  </Box>
                  {logo ? (
                    <Box component="button" type="button" onClick={() => { setLogo(null); setLogoNote(''); }} sx={{ ...pillSx(false), height: 30, fontSize: 13 }}>
                      {EXPORT_TEXT.LOGO_REMOVE}
                    </Box>
                  ) : null}
                </Box>
                {logo ? (
                  <>
                    <Box role="group" aria-label={EXPORT_TEXT.LOGO_PLACE} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', fontSize: 13 }}>
                      <Box component="span" sx={{ minWidth: 80 }}>{EXPORT_TEXT.LOGO_PLACE}</Box>
                      {LOGO_SPOTS.map((spot) => (
                        <Box
                          key={spot.key}
                          component="button"
                          type="button"
                          title={spot.title}
                          aria-label={spot.title}
                          aria-pressed={logo.spot === spot.key}
                          onClick={() => setLogo((l) => ({ ...l, spot: spot.key }))}
                          sx={{ ...pillSx(logo.spot === spot.key), width: 34, height: 30, p: 0, fontSize: 15, justifyContent: 'center' }}
                        >
                          {spot.label}
                        </Box>
                      ))}
                    </Box>
                    {shape === 'blur' ? (
                      <Box role="group" aria-label={EXPORT_TEXT.LOGO_ON_PICTURE} sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 13, pl: '88px', flexWrap: 'wrap' }}>
                        {[['picture', EXPORT_TEXT.LOGO_ON_PICTURE], ['frame', EXPORT_TEXT.LOGO_ON_FRAME]].map(([key, text]) => {
                          const on = (logo.on || 'picture') === key;
                          return (
                            <Box
                              key={key}
                              component="button"
                              type="button"
                              aria-pressed={on}
                              onClick={() => setLogo((l) => ({ ...l, on: key, spot: l.spot || 'tr' }))}
                              sx={{ ...pillSx(on), height: 28, fontSize: 12 }}
                            >
                              {text}
                            </Box>
                          );
                        })}
                      </Box>
                    ) : null}
                    <Box role="group" aria-label={EXPORT_TEXT.LOGO_BACKDROP} sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 13, flexWrap: 'wrap' }}>
                      <Box component="span" sx={{ minWidth: 80 }}>{EXPORT_TEXT.LOGO_BACKDROP}</Box>
                      {LOGO_BACKDROPS.map((b) => {
                        const on = (logo.backdrop || 'none') === b.key;
                        return (
                          <Box
                            key={b.key}
                            component="button"
                            type="button"
                            aria-pressed={on}
                            onClick={() => setLogo((l) => ({ ...l, backdrop: b.key }))}
                            sx={{ ...pillSx(on), height: 28, fontSize: 12 }}
                          >
                            {b.label}
                          </Box>
                        );
                      })}
                    </Box>
                    <Box component="label" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontSize: 13 }}>
                      <Box component="span" sx={{ minWidth: 80 }}>{EXPORT_TEXT.LOGO_SIZE}</Box>
                      <Box
                        component="input"
                        type="range"
                        min={4}
                        max={60}
                        step={1}
                        value={Math.round(logo.size * 100)}
                        aria-label={EXPORT_TEXT.LOGO_SIZE}
                        onChange={(e) => setLogo((l) => ({ ...l, size: Number(e.target.value) / 100 }))}
                        sx={sliderSx(((logo.size * 100 - 4) / 56) * 100)}
                      />
                    </Box>
                    <Box component="label" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontSize: 13 }}>
                      <Box component="span" sx={{ minWidth: 80 }}>{EXPORT_TEXT.LOGO_OPACITY}</Box>
                      <Box
                        component="input"
                        type="range"
                        min={10}
                        max={100}
                        step={5}
                        value={Math.round(logo.opacity * 100)}
                        aria-label={EXPORT_TEXT.LOGO_OPACITY}
                        onChange={(e) => setLogo((l) => ({ ...l, opacity: Number(e.target.value) / 100 }))}
                        sx={sliderSx(((logo.opacity * 100 - 10) / 90) * 100)}
                      />
                    </Box>
                  </>
                ) : null}
              </Box>
            ) : null}

            {makesSupercut || takesRoom ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, p: '14px 16px', borderRadius: '14px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <Box>
                  <Box sx={{ fontSize: 15, fontWeight: 600 }}>{makesSupercut ? EXPORT_TEXT.SUPERCUT : EXPORT_TEXT.ROOM}</Box>
                  <Box sx={{ fontSize: 12, color: MUTED }}>{makesSupercut ? EXPORT_TEXT.SUPERCUT_HINT : EXPORT_TEXT.ROOM_HINT}</Box>
                </Box>
                <Box component="label" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontSize: 13 }}>
                  <Box component="span" sx={{ minWidth: 110 }}>{makesSupercut ? EXPORT_TEXT.SUPERCUT_PAD : EXPORT_TEXT.ROOM_ADD}</Box>
                  <Box
                    component="input"
                    type="range"
                    min={0}
                    max={500}
                    step={10}
                    value={padMs}
                    aria-label={EXPORT_TEXT.SUPERCUT_PAD}
                    onChange={(e) => setPadMs(Number(e.target.value))}
                    sx={sliderSx((padMs / 500) * 100)}
                  />
                  <Box component="span" sx={{ fontSize: 12, color: MUTED, minWidth: 170 }}>
                    {makesSupercut && !takesRoom ? EXPORT_TEXT.supercutPad(padMs) : EXPORT_TEXT.roomPad(padMs, makesSupercut)}
                  </Box>
                </Box>
                {makesSupercut ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ fontSize: 13, fontWeight: 600 }}>{EXPORT_TEXT.SUPERCUT_EQUALIZE}</Box>
                    <Box sx={{ fontSize: 12, color: MUTED }}>{EXPORT_TEXT.SUPERCUT_EQUALIZE_HINT}</Box>
                  </Box>
                  <Box
                    component="button"
                    aria-label={EXPORT_TEXT.SUPERCUT_EQUALIZE}
                    aria-pressed={equalize}
                    onClick={() => setEqualize((v) => !v)}
                    sx={{ width: 48, height: 28, borderRadius: '14px', border: 'none', cursor: 'pointer', position: 'relative', p: 0, flexShrink: 0, background: equalize ? CHERRY : 'rgba(255,255,255,0.15)', '&:focus-visible': { outline: '2px solid #ff6b8b', outlineOffset: '2px' } }}
                  >
                    <Box component="span" sx={{ position: 'absolute', top: 3, left: equalize ? 23 : 3, width: 22, height: 22, borderRadius: '11px', background: '#fff', transition: 'left 120ms' }} />
                  </Box>
                </Box>
                ) : null}
              </Box>
            ) : null}

            {/* "More options ↓" floats over the list in a ZERO-height sticky anchor, so it takes no
                space: the list's height never changes when it shows/hides (no bounce), and it can
                actually disappear once you're at the bottom. Clicking it scrolls down. */}
            {/* The list's bottom padding (24px) would sit UNDER the sticky anchor and peek out below
                the pill, so the fade reaches past it to the window's edge (pill stays put). */}
            <Box sx={{ position: 'sticky', bottom: 0, height: 0, mx: -3.5, zIndex: 2, pointerEvents: 'none' }}>
              <Box
                aria-hidden={!moreBelow}
                sx={{ position: 'absolute', left: 0, right: 0, bottom: '-24px', pt: 5, pb: '36px', display: 'flex', justifyContent: 'center', background: 'linear-gradient(180deg, rgba(26,23,31,0) 0%, #1a171f 55%)', opacity: moreBelow ? 1 : 0, visibility: moreBelow ? 'visible' : 'hidden', transition: 'opacity 150ms' }}
              >
                <Box
                  component="button"
                  onClick={scrollDown}
                  tabIndex={moreBelow ? 0 : -1}
                  sx={{ pointerEvents: moreBelow ? 'auto' : 'none', font: 'inherit', fontSize: 13, fontWeight: 600, color: '#fff', height: 34, px: 2, borderRadius: '17px', border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(40,34,46,0.95)', cursor: 'pointer', '&:hover': { borderColor: CHERRY } }}
                >
                  {EXPORT_TEXT.MORE_BELOW} ↓
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* ---- right: what you'll get ---- */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', background: 'radial-gradient(circle at 50% 35%, #231d29 0%, #141117 70%)' }}>
          {/* Share link sits on the PREVIEW line (at the bottom, the Framing controls pushed it out
              of the window; the Premiere link went beside the Export button). Negative margins
              keep this line its old height, and the shared link floats below — nothing moves. */}
          <Box sx={{ position: 'relative', px: 3.5, pt: 2.75, display: 'flex', gap: 1, alignItems: 'center' }}>
            <Box component="span" sx={label}>{EXPORT_TEXT.PREVIEW}</Box>
            <Box component="span" sx={{ flex: 1, minWidth: 0, fontSize: 12, color: FAINT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              · {focusCard ? `${focusCard.label}${focusCard.ext ? ` (${focusCard.ext})` : ''}` : EXPORT_TEXT.PREVIEW_NOTHING}
            </Box>
            {onShareLink ? (
              <Box
                component="button"
                title={EXPORT_TEXT.SHARE_LINK_HINT}
                onClick={doShare}
                sx={{ flexShrink: 0, my: '-7px', font: 'inherit', fontSize: 13, fontWeight: 600, height: 30, px: 1.5, borderRadius: '10px', border: '1px solid rgba(255,255,255,0.14)', background: 'transparent', color: '#f4f1f6', cursor: 'pointer', whiteSpace: 'nowrap', '&:hover': { borderColor: CHERRY } }}
              >
                {EXPORT_TEXT.SHARE_LINK}
              </Box>
            ) : null}
            {shared ? (
              <Box sx={{ position: 'absolute', top: '100%', left: 28, right: 28, mt: 1, zIndex: 3, p: '10px 12px', borderRadius: '12px', background: '#0f0d12', border: `1px solid ${CHERRY}`, boxShadow: '0 12px 30px rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box component="span" sx={{ flex: 1, minWidth: 0, fontSize: 13, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', userSelect: 'all' }}>{shared.url}</Box>
                <Box component="button" onClick={copyShared} sx={{ ...pillSx(shared.copied), height: 30 }}>{shared.copied ? EXPORT_TEXT.COPIED : EXPORT_TEXT.COPY_LINK}</Box>
                <Box component="a" href={shared.url} target="_blank" rel="noopener noreferrer" sx={{ fontSize: 13, fontWeight: 600, color: '#ff8aa3', textDecoration: 'none', px: 0.5 }}>{EXPORT_TEXT.OPEN_LINK}</Box>
                <Box component="button" aria-label={EXPORT_TEXT.HIDE_LINK} title={EXPORT_TEXT.HIDE_LINK} onClick={() => setShared(null)} sx={{ width: 28, height: 28, borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: 'inherit', fontSize: 15, cursor: 'pointer' }}>×</Box>
              </Box>
            ) : null}
          </Box>
          {/* Fixed height: framing, picks or summary changing below can never resize (jump) it.
              ✏ Edit words and its box FLOAT over the preview, so nothing else moves. */}
          <Box sx={{ position: 'relative', flex: '0 0 auto', height: { xs: 260, md: 300 }, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2.5, py: 1.5 }}>
            {focusCard && ['video', 'super', 'audiogram', 'gif'].includes(focusCard.key) ? (
              <Box
                component="button"
                aria-pressed={playing && canPlay}
                title={EXPORT_TEXT.PLAY_CLIP_HINT}
                disabled={!canPlay}
                onClick={() => setPlaying((v) => !v)}
                sx={{ ...pillSx(false), position: 'absolute', top: 10, left: 22, zIndex: 2, height: 30, fontSize: 12, width: 104, px: 0, background: 'rgba(20,17,23,0.88)', opacity: canPlay ? 1 : 0.35, cursor: canPlay ? 'pointer' : 'default' }}
              >
                {playing && canPlay ? EXPORT_TEXT.STOP_CLIP : EXPORT_TEXT.PLAY_CLIP}
              </Box>
            ) : null}
            {canEditText && editingText === null ? (
              <Box
                component="button"
                onClick={startEditing}
                sx={{ ...pillSx(false), position: 'absolute', top: 10, right: 22, zIndex: 2, height: 30, fontSize: 12, background: 'rgba(20,17,23,0.88)' }}
              >
                {EXPORT_TEXT.EDIT_WORDS}
              </Box>
            ) : null}
            {editingText !== null ? (
              <Box data-testid="export-edit-words" sx={{ position: 'absolute', left: 20, right: 20, bottom: 12, zIndex: 3, p: 1.25, borderRadius: '12px', background: 'rgba(15,13,18,0.97)', border: `1px solid ${CHERRY}`, boxShadow: '0 12px 30px rgba(0,0,0,0.55)' }}>
                <Box
                  component="textarea"
                  autoFocus
                  rows={2}
                  aria-label={EXPORT_TEXT.EDIT_WORDS_LABEL}
                  value={editingText}
                  onChange={(e) => setEditingText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveEditing(); }
                    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setEditingText(null); }
                  }}
                  sx={{ width: '100%', boxSizing: 'border-box', resize: 'none', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.12)', background: '#121015', color: '#f4f1f6', p: 1, font: 'inherit', fontSize: 14, '&:focus': { outline: 'none', borderColor: CHERRY } }}
                />
                <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box component="span" sx={{ flex: 1, minWidth: 0, fontSize: 11, color: FAINT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{EXPORT_TEXT.EDIT_HINT}</Box>
                  {shown.edited ? (
                    <Box component="button" onClick={() => { onEditText(shown.id, null); setEditingText(null); }} sx={{ ...pillSx(false), height: 28, fontSize: 12 }}>{EXPORT_TEXT.EDIT_RESET}</Box>
                  ) : null}
                  <Box component="button" onClick={() => setEditingText(null)} sx={{ ...pillSx(false), height: 28, fontSize: 12 }}>{EXPORT_TEXT.EDIT_CANCEL}</Box>
                  <Box component="button" onClick={saveEditing} sx={{ ...pillSx(true), height: 28, fontSize: 12 }}>{EXPORT_TEXT.EDIT_SAVE}</Box>
                </Box>
              </Box>
            ) : null}
            <ExportPreview
              kind={focusCard?.key}
              shape={shape}
              thumbnail={shown.thumbnail}
              text={shown.text}
              speaker={shown.speaker}
              start={shown.start}
              count={count}
              captions={captions}
              mode={mode}
              wordsPerGroup={wordsPerGroup}
              palette={palette}
              size={size}
              font={CAPTION_FONTS.find((f) => f.key === fontKey)}
              look={lookKey}
              upper={upper}
              highlight={highlightKey}
              reveal={revealKey}
              bounce={bounceKey}
              preset={presetKey}
              nouns={shown.nouns || ''}
              view={currentView}
              onViewChange={currentId !== null && makesVideo ? setView : null}
              captionPos={currentLayout.caption || null}
              onCaptionMove={captions && makesVideo ? moveCaption : null}
              logo={makesVideo ? logo : null}
              onLogoMove={makesVideo && logo
                // a snap keeps the quick spot; a drag is free placement
                ? (pos, opts) => setLogo((l) => ({ ...l, ...pos, ...(opts && opts.snap ? {} : { spot: null }) }))
                : null}
              onCaptionEdit={canEditText ? startEditing : null}
              wordStarts={shown.wordStarts || null}
              seconds={shown.seconds || 0}
              playing={playing && canPlay}
              videoUrl={shown.video || ''}
            />
          </Box>
          {/* If these don't fit (a small screen, several formats ticked), THEY scroll — the
              Export button below always stays in the window. When they fit, nothing changes. */}
          <Box data-testid="export-right-middle" sx={{ flex: { md: '0 1 auto' }, minHeight: { md: 0 }, overflowY: { md: 'auto' } }}>
          {makesVideo && currentId !== null && focusCard && focusCard.key !== 'audiogram' ? (
            // Fixed layout: every control is always there in the same place (Reset greys out),
            // and no text appears or disappears — nothing shifts when you click.
            <Box data-testid="export-framing" sx={{ mx: 3.5, mb: 1.25, display: 'grid', gridTemplateColumns: '1fr auto auto auto', columnGap: 1, rowGap: 0.75, alignItems: 'center' }}>
              <Box sx={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
                <Box component="span" sx={label}>{EXPORT_TEXT.FRAMING}</Box>
                <Box component="span" sx={{ fontSize: 12, color: FAINT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {EXPORT_TEXT.clipOf(shownIndex + 1, picks.length)} · {EXPORT_TEXT.FRAMING_HINT}
                </Box>
              </Box>
              <Box component="label" sx={{ display: 'flex', alignItems: 'center', gap: 1.25, fontSize: 13, minWidth: 0 }}>
                {EXPORT_TEXT.ZOOM}
                <Box
                  component="input"
                  type="range"
                  min={1}
                  max={MAX_ZOOM}
                  step={0.05}
                  value={currentView.zoom}
                  aria-label={EXPORT_TEXT.ZOOM}
                  onChange={(e) => setView({ ...currentView, zoom: Number(e.target.value) })}
                  sx={sliderSx(((currentView.zoom - 1) / (MAX_ZOOM - 1)) * 100)}
                />
                <Box component="span" sx={{ fontSize: 12, color: MUTED, width: 34, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{EXPORT_TEXT.zoomLabel(currentView.zoom)}</Box>
              </Box>
              <Box component="button" title={EXPORT_TEXT.FACE_HINT} aria-pressed={currentView.face} onClick={toggleFace} sx={{ ...pillSx(currentView.face), height: 30, fontSize: 12, width: 132 }}>
                🎯 {EXPORT_TEXT.FACE}
              </Box>
              <Box component="button" onClick={resetClip} disabled={!isCustomLayout(layouts[currentId])} sx={{ ...pillSx(false), height: 30, fontSize: 12, width: 84, opacity: isCustomLayout(layouts[currentId]) ? 1 : 0.35, cursor: isCustomLayout(layouts[currentId]) ? 'pointer' : 'default' }}>
                {EXPORT_TEXT.RESET_CLIP}
              </Box>
              <Box component="button" onClick={resetAll} disabled={!(framedCount > 0)} sx={{ ...pillSx(false), height: 30, fontSize: 12, width: 78, opacity: framedCount > 0 ? 1 : 0.35, cursor: framedCount > 0 ? 'pointer' : 'default' }}>
                {EXPORT_TEXT.RESET_ALL}
              </Box>
              <Box sx={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box component="span" sx={{ fontSize: 12, color: MUTED }}>{EXPORT_TEXT.COPY_WHAT}</Box>
                {[['picture', EXPORT_TEXT.COPY_PICTURE, true], ['captions', EXPORT_TEXT.COPY_CAPTIONS, captions]].map(([part, text, usable]) => (
                  <Box
                    component="label"
                    key={part}
                    title={usable ? '' : EXPORT_TEXT.COPY_CAPTIONS_OFF}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 12, whiteSpace: 'nowrap', color: usable ? '#d9d2de' : FAINT, cursor: usable ? 'pointer' : 'default' }}
                  >
                    <Box
                      component="input"
                      type="checkbox"
                      checked={usable && copyParts[part]}
                      disabled={!usable}
                      onChange={() => setCopyParts((v) => ({ ...v, [part]: !v[part] }))}
                      sx={{ m: 0, accentColor: '#8d8594', cursor: 'inherit' }}
                    />
                    {text}
                  </Box>
                ))}
                <Box component="button" onClick={copyToNext} disabled={shownIndex >= picks.length - 1 || !copyAnything} sx={{ ...pillSx(false), height: 30, fontSize: 12, flex: 1, whiteSpace: 'nowrap', px: 1, opacity: shownIndex >= picks.length - 1 || !copyAnything ? 0.35 : 1 }}>
                  {EXPORT_TEXT.COPY_NEXT} →
                </Box>
                <Box component="button" onClick={applyToAll} disabled={picks.length < 2 || !copyAnything} sx={{ ...pillSx(false), height: 30, fontSize: 12, flex: 1, whiteSpace: 'nowrap', px: 1, opacity: picks.length < 2 || !copyAnything ? 0.35 : 1 }}>
                  {EXPORT_TEXT.APPLY_ALL}
                </Box>
              </Box>
            </Box>
          ) : null}
          {picks.length ? (
            <Box sx={{ mx: 3.5, mb: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', mb: 1 }}>
                <Box component="span" sx={label}>{EXPORT_TEXT.PICKS}</Box>
                <Box component="span" sx={{ fontSize: 12, color: FAINT }}>{EXPORT_TEXT.PICKS_HINT}</Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5 }}>
                {picks.map((p, i) => {
                  const on = shown && shown.id === p.id;
                  const gap = i > 0 ? (TRANSITIONS.find((x) => x.key === (gapsOn ? gapOf(i - 1) : 'none')) || TRANSITIONS[0]) : null;
                  return (
                    <React.Fragment key={p.id}>
                    {gap ? (
                      // The join into this pick: always here (greyed when there's no stitched video), so nothing moves.
                      <Box
                        component="button"
                        data-testid="export-gap"
                        aria-label={`${EXPORT_TEXT.GAP_MENU} ${i} → ${i + 1}: ${gap.label}`}
                        title={gapsOn ? `${EXPORT_TEXT.GAP_MENU}: ${gap.label}` : EXPORT_TEXT.GAP_OFF}
                        disabled={!gapsOn}
                        onClick={(e) => setGapMenu({ anchor: e.currentTarget, index: i - 1 })}
                        sx={{ flex: '0 0 auto', alignSelf: 'flex-start', mt: '24px', height: 24, px: 0.75, borderRadius: '12px', font: 'inherit', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap', cursor: gapsOn ? 'pointer' : 'default', opacity: gapsOn ? 1 : 0.35, color: gap.key !== 'none' ? '#ff8aa3' : MUTED, background: gap.key !== 'none' ? 'rgba(236,43,87,0.14)' : 'transparent', border: `1px solid ${gap.key !== 'none' ? 'rgba(236,43,87,0.5)' : 'rgba(255,255,255,0.14)'}`, '&:hover': gapsOn ? { borderColor: CHERRY } : {}, '&:focus-visible': { outline: '2px solid #ff6b8b', outlineOffset: '2px' } }}
                      >
                        {gap.short}
                      </Box>
                    ) : null}
                    <Box
                      component="button"
                      data-testid="export-pick"
                      title={p.text}
                      onClick={() => setPreviewId(p.id)}
                      sx={{ flex: '0 0 116px', p: 0.75, borderRadius: '10px', font: 'inherit', color: 'inherit', textAlign: 'left', cursor: 'pointer', background: on ? 'rgba(236,43,87,0.13)' : 'rgba(255,255,255,0.03)', border: `1.5px solid ${on ? CHERRY : 'rgba(255,255,255,0.08)'}` }}
                    >
                      <Box sx={{ position: 'relative', height: 58, borderRadius: '6px', overflow: 'hidden', background: '#000' }}>
                        <PickThumb
                          src={p.thumbnail}
                          layout={layouts[p.id]}
                          captions={captions && makesVideo}
                          text={p.text}
                          palette={palette}
                          vertical={shape === 'blur' || shape === 'zoom'}
                          mode={mode}
                          wordsPerGroup={wordsPerGroup}
                          font={CAPTION_FONTS.find((f) => f.key === fontKey)}
                          look={lookKey}
                          upper={upper}
                          highlight={highlightKey}
                          reveal={revealKey}
                          bounce={bounceKey}
                        />
                        <Box component="span" sx={{ position: 'absolute', top: 4, left: 4, minWidth: 18, height: 18, px: 0.5, borderRadius: '9px', background: CHERRY, color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</Box>
                        {isCustomLayout(layouts[p.id]) ? (
                          <Box component="span" title={EXPORT_TEXT.FRAMED_MARK} data-testid="export-pick-framed" sx={{ position: 'absolute', top: 4, right: 4, height: 18, px: 0.5, borderRadius: '9px', background: 'rgba(0,0,0,0.75)', color: '#fff', fontSize: 11, display: 'flex', alignItems: 'center' }}>✥</Box>
                        ) : null}
                      </Box>
                      <Box sx={{ mt: 0.5, fontSize: 11, color: MUTED, display: 'flex', justifyContent: 'space-between', gap: 0.5 }}>
                        <span>{p.speaker ? (/^[A-Z]$/.test(String(p.speaker).trim()) ? `Speaker ${String(p.speaker).trim()}` : p.speaker) : ''}</span>
                        <span>{formatDuration((p.seconds || 0) * 1000)}</span>
                      </Box>
                      <Box sx={{ fontSize: 12, lineHeight: '15px', mt: 0.25, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{p.text}</Box>
                    </Box>
                    </React.Fragment>
                  );
                })}
              </Box>
              <Menu
                open={Boolean(gapMenu) && gapsOn && gapMenu.index < picks.length - 1}
                anchorEl={gapMenu ? gapMenu.anchor : null}
                onClose={() => setGapMenu(null)}
                disableScrollLock
                MenuListProps={{ 'aria-label': EXPORT_TEXT.GAP_MENU, dense: true }}
                PaperProps={{ sx: { background: '#1f1b24', color: '#f4f1f6', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px' } }}
              >
                {gapMenu && gapMenu.index < picks.length - 1 ? TRANSITIONS.map((t) => (
                  <MenuItem
                    key={t.key}
                    selected={gapOf(gapMenu.index) === t.key}
                    onClick={() => { setGap(gapMenu.index, t.key); setGapMenu(null); }}
                    sx={{ fontSize: 13, '&.Mui-selected': { background: 'rgba(236,43,87,0.22)' }, '&.Mui-selected:hover': { background: 'rgba(236,43,87,0.3)' } }}
                  >
                    {t.label}
                  </MenuItem>
                )) : null}
              </Menu>
            </Box>
          ) : null}
          <Box sx={{ mx: 3.5, p: '14px 16px', borderRadius: '14px', background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.07)', display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box component="span" sx={label}>{EXPORT_TEXT.YOU_GET}</Box>
            {summary.map((r) => (
              <Box key={r.name} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, fontSize: 14, flexWrap: 'wrap' }}>
                <Box component="span" sx={{ minWidth: 44, textAlign: 'center', borderRadius: '6px', background: 'rgba(236,43,87,0.16)', color: '#ff8aa3', fontSize: 11, fontWeight: 700, py: '2px' }}>
                  {r.ext}
                </Box>
                <Box component="span" sx={{ fontWeight: 600 }}>{r.label}</Box>
                <Box component="span" sx={{ color: MUTED }}>{r.detail}</Box>
              </Box>
            ))}
            {!summary.length ? <Box component="span" sx={{ fontSize: 14, color: MUTED }}>{EXPORT_TEXT.PICK_ONE}</Box> : null}
            {canUseMyStyle() ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', mt: 0.5 }}>
                <Box
                  component="button"
                  type="button"
                  onClick={doSaveStyle}
                  title={EXPORT_TEXT.STYLE_HINT}
                  sx={{
                    height: 30,
                    px: 1.5,
                    borderRadius: '8px',
                    border: '1px solid rgba(236,43,87,0.45)',
                    background: myStyle ? 'rgba(236,43,87,0.16)' : 'transparent',
                    color: '#ff8aa3',
                    font: 'inherit',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    '&:hover': { color: '#fff', borderColor: CHERRY },
                    '&:focus-visible': { outline: '2px solid #fff', outlineOffset: '2px' },
                  }}
                >
                  {EXPORT_TEXT.SAVE_STYLE}
                </Box>
                <Box component="span" sx={{ fontSize: 12, color: MUTED, minWidth: 0, flex: 1 }}>
                  {styleNote || EXPORT_TEXT.STYLE_HINT}
                </Box>
              </Box>
            ) : null}
          </Box>
          </Box>
          <Box sx={{ flexShrink: 0, px: 3.5, pt: 2, pb: { xs: 2, md: 1.25 }, display: 'flex', alignItems: 'center', gap: 1.75, position: { xs: 'sticky', md: 'static' }, bottom: 0, background: { xs: '#17141b', md: 'transparent' }, borderTop: { xs: '1px solid rgba(255,255,255,0.07)', md: 'none' }, zIndex: 1 }}>
            {/* Two short lines beside the 48px button — the row keeps its height. */}
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              <Box component="span" sx={{ fontSize: 12, color: FAINT }}>{EXPORT_TEXT.LANDS}</Box>
              <Box
                component="a"
                href={ROUTES.PLUGIN_DOWNLOAD}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ alignSelf: 'flex-start', fontSize: 13, lineHeight: '16px', fontWeight: 600, color: '#ff8aa3', textDecoration: 'none', '&:hover': { color: '#fff' } }}
              >
                {EXPORT_TEXT.PREMIERE_PANEL}
              </Box>
            </Box>
            <Box
              component="button"
              onClick={doExport}
              disabled={!chosen.length}
              sx={{
                height: 48,
                px: 3.25,
                border: 'none',
                borderRadius: '12px',
                background: chosen.length ? 'linear-gradient(180deg, #f0386a 0%, #d81f4e 100%)' : 'rgba(255,255,255,0.1)',
                color: '#fff',
                font: 'inherit',
                fontSize: 16,
                fontWeight: 700,
                cursor: chosen.length ? 'pointer' : 'not-allowed',
                boxShadow: chosen.length ? '0 8px 22px rgba(236,43,87,0.35)' : 'none',
                '&:focus-visible': { outline: '2px solid #fff', outlineOffset: '2px' },
              }}
            >
              {exportLabel}
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ExportWindow;
